import React, { useEffect } from "react";
import { Col } from "react-bootstrap";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../../assets/layouts";
import {
  SortDirection,
  SortModel,
} from "../../../../assets/utils/lib/searchDto";
import Form from "../../../../assets/vfx/form/form";
import { NonprofitOrganizationService } from "./organizationService";
import { Constants } from "../../../../assets/utils/lib/constants";
import { GRID_ACTION_TYPES, SetState } from "../../../../assets/types";

import { SearchContainer } from "../../../../assets/vfx/search/searchContainer";
import { VfxLib } from "../../../../assets/vfx";
import Control from "../../../../assets/vfx/controls/control";
import { ViewProps } from "./props";
import { useCommunity } from "../../../../context/communityContext";
import { OrganizationGridDto } from "../../../../services/dto/nonprofit/organizationGridDto";

interface NonprofitOrganizationPageProps {
  controlStyles?: IControlStyles;
  lookups?: ViewProps.OrganizationLookupProps;
  communityId?: string;
  hideTitle?: boolean;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  setCommunity: SetState<string | undefined>;
}

export interface NonprofitOrganizationSearchProps {
  communityId: string;
}

const defaultSortModel: SortModel = {
  field: "name",
  direction: SortDirection.Ascending,
};

export const isNewRequest = (status: string | undefined): boolean => {
  return ["new-request", "New"].includes(status ?? "");
};

export interface SearchOrganizationDto {
  communityId?: string | undefined;
  categoryId?: string | undefined;
  name?: string | undefined;
  sortBy: SortModel;
  pageSize?: number | undefined;
  pageNumber?: number | undefined;
}

export const NonprofitOrganizationPage = (
  props: NonprofitOrganizationPageProps
) => {
  const communityContext = useCommunity();
  let communityId = props.communityId;
  if (!communityId) communityId = communityContext.communityId ?? undefined;

   const defaultSearchQuery: SearchOrganizationDto = {
      pageSize: 10,
      pageNumber: 1,
      sortBy: defaultSortModel,
      name: Constants.emptyString,
      ...(communityId && { communityId: communityId }),
    };


  const [searchQuery, setSearchQuery] =
    React.useState<SearchOrganizationDto>(defaultSearchQuery);

  useEffect(() => {
    if (communityId && communityId !== Constants.emptyGuid) {
      setSearchQuery((q) => ({ ...q, communityId }));
    }
  }, [communityId]);

  const styles: IControlStyles = props.controlStyles ?? useControlStyles();

  const nonprofitOrganizationGridQuery =
    NonprofitOrganizationService.gridQuery(searchQuery);
  const isGridLoading =
    nonprofitOrganizationGridQuery.isLoading || nonprofitOrganizationGridQuery.isFetching;

    const handleSearch = (values: SearchOrganizationDto): void => {
      const updatedSearchValues: SearchOrganizationDto = {
        ...defaultSearchQuery,
        pageSize: searchQuery.pageSize,
        pageNumber: searchQuery.pageNumber ?? 1,      
        name: values.name,
        ...(values.categoryId && { categoryId: values.categoryId }),
      };
  
      setSearchQuery(updatedSearchValues);
  
      if (communityId) {
        props.setCommunity(communityId);
      }
    };


    const handleClear = (): void => {
        setSearchQuery(prev => ({
        ...defaultSearchQuery,
        sortBy: prev.sortBy,
        pageSize: prev.pageSize ?? 10,   
    }));
  };

  const handleNew = (): void | undefined =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  const searchContainer: React.JSX.Element = (
    <SearchContainer
      initialValues={{
        categoryId: Constants.emptyString,
         name: Constants.emptyString
      }}
      enableReinitialize
      onSearch={handleSearch}
      onClear={handleClear}
    >
      <Col lg={3}>
        <Form.Dropdown
          name="categoryId"
          label="Category"
          blankOption={{
            key: Constants.emptyGuid,
            display: "Select Category",
            value: Constants.emptyString,
          }}
          options={props.lookups?.category}
        />
      </Col>
      <Col lg={3} className="ms-2">
        <Form.Text name="name" label="Name" />
      </Col>
    </SearchContainer>
  );

  const nonprofitOrganizationPageContent: React.JSX.Element = (
    <>
        <Control.DataGrid
          dataId="organizationId"
          height="55vh"
          cols={[
            {
              data: "organizationId",
              title: "ID",
              visible: false,
              columnType: "number",
              alignment: "right",
              sortable: false,
            },
            {
              data: "name",
              title: "Name",
              visible: true,
              columnType: "text",
              alignment: "left",
              sortable: true,
              width: "30%",
            },
            {
              data: "categoryIds",
              title: "Category",
              visible: true,
              columnType: "custom",
              alignment: "left",
              sortable: false,
              width: "40%",
              customCell: (id: string, rowData: OrganizationGridDto) => {
                return rowData?.categoryIds
                  ?.map(
                    (category) =>
                      props.lookups?.category.find((x) => x.value === category)
                        ?.display
                  )
                  .filter(Boolean)
                  .join(", ");
              },
            },
            {
              data: "activationDate",
              title: "Activation Date",
              visible: true,
              columnType: "date",
              alignment: "left",
              width: "20%",
            },
          ]}
          actionCol={{
            data: "id",
            title: "Action",
            visible: true,
            columnType: "action",
            alignment: "right",
            width: "100px",
            controlStyles: styles,
            actionButtons: [
              VfxLib.datagridActionButtons({ datagridStyles: styles }).view,
              VfxLib.datagridActionButtons({ datagridStyles: styles }).delete,
            ],
          }}
          onCustomEvent={props.onCustomEvent}
          data={nonprofitOrganizationGridQuery?.data?.result ?? []}
          isActionButtonClick={true}
           totalRecords={nonprofitOrganizationGridQuery?.data?.totalSize ?? (nonprofitOrganizationGridQuery?.data?.result?.length ?? 0)}
          query={searchQuery}
          loading={isGridLoading}
          onQueryChange={(patch) => setSearchQuery(query => ({ ...query, ...patch }))}
          uiOptions={{
            title: props.hideTitle ? undefined : "Nonprofit Organization",
            filterContent: searchContainer,
            primaryAction: {
              text: "Add Nonprofit Organization",
              icon: "bi bi-plus-lg",
              onClick: handleNew,
            },
          }}

        />
    </>
  );

  return props.hideTitle ? (
    nonprofitOrganizationPageContent
  ) : (
    <PageContent hideHeader>
      {nonprofitOrganizationPageContent}
    </PageContent>
  );
};
